<template>
  <div class="component-testjsoneditor">
    <v-card flat>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center mb-3">
              <v-icon left>mdi-code-json</v-icon>
              <span class="text-h6">{{ $t('test.editor.jsonEditor.title') }}</span>

              <v-chip
                :color="isReadonly ? 'orange' : 'success'"
                text-color="white"
                small
                class="ml-3"
              >
                <v-icon left x-small>
                  {{ isReadonly ? 'mdi-eye' : 'mdi-pencil' }}
                </v-icon>
                {{ isReadonly ? $t('test.editor.jsonEditor.viewMode') : $t('test.editor.jsonEditor.editMode') }}
              </v-chip>

              <v-spacer></v-spacer>

              <!-- Switch Modalità Modifica -->
              <div class="d-flex align-center mr-3">
                <v-icon
                  small
                  :color="isReadonly ? 'grey' : 'success'"
                  class="mr-2"
                >
                  {{ isReadonly ? 'mdi-lock' : 'mdi-lock-open' }}
                </v-icon>
                <v-switch
                  v-model="editMode"
                  :label="editMode ? $t('test.editor.jsonEditor.editModeActive') : $t('test.editor.jsonEditor.readonlyMode')"
                  color="success"
                  dense
                  hide-details
                  class="mt-0"
                ></v-switch>
              </div>

              <v-btn
                v-if="!isValidJson && !isReadonly"
                color="error"
                text
                small
                @click="formatJson"
              >
                <v-icon left small>mdi-auto-fix</v-icon>
                {{ $t('test.editor.jsonEditor.fixFormat') }}
              </v-btn>

              <v-btn
                v-else-if="!isReadonly"
                color="success"
                text
                small
                @click="formatJson"
              >
                <v-icon left small>mdi-code-braces</v-icon>
                {{ $t('test.editor.jsonEditor.format') }}
              </v-btn>

              <v-btn
                color="primary"
                text
                small
                @click="copyToClipboard"
              >
                <v-icon left small>mdi-content-copy</v-icon>
                {{ $t('test.editor.jsonEditor.copy') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-alert
              v-if="jsonError && !isReadonly"
              type="error"
              text
              dismissible
              class="mb-3"
            >
              <strong>{{ $t('test.editor.jsonEditor.jsonError') }}</strong> {{ jsonError }}
            </v-alert>

            <v-alert
              v-else-if="isValidJson && !isReadonly"
              type="success"
              text
              class="mb-3"
            >
              <v-icon left>mdi-check-circle</v-icon>
              {{ $t('test.editor.jsonEditor.jsonValid') }}
            </v-alert>

            <v-alert
              v-else-if="isReadonly"
              type="info"
              text
              class="mb-3"
            >
              <v-icon left>mdi-information</v-icon>
              {{ $t('test.editor.jsonEditor.viewModeAlert') }}
            </v-alert>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="localJson"
              :label="isReadonly ? $t('test.editor.jsonEditor.labelReadonly') : $t('test.editor.jsonEditor.labelEditable')"
              rows="25"
              outlined
              auto-grow
              :readonly="isReadonly"
              :error-messages="jsonError && !isReadonly ? [jsonError] : []"
              :success="isValidJson && !jsonError && !isReadonly"
              :placeholder="isReadonly ? $t('test.editor.jsonEditor.placeholderReadonly') : $t('test.editor.jsonEditor.placeholderEditable')"
              :class="{ 'json-editor': true, 'json-editor--readonly': isReadonly }"
              @input="onJsonChange"
              @blur="validateAndEmit"
            >
              <template v-slot:prepend-inner>
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      :color="isReadonly ? 'grey' : (isValidJson ? 'success' : 'error')"
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ isReadonly ? 'mdi-lock' : (isValidJson ? 'mdi-check-circle' : 'mdi-alert-circle') }}
                    </v-icon>
                  </template>
                  <span>
                    {{ isReadonly ? $t('test.editor.jsonEditor.tooltipReadonly') : (isValidJson ? $t('test.editor.jsonEditor.tooltipValid') : jsonError) }}
                  </span>
                </v-tooltip>
              </template>
            </v-textarea>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center text--secondary">
              <v-icon small left>mdi-information</v-icon>
              <span class="caption">
                <template v-if="isReadonly">
                  {{ $t('test.editor.jsonEditor.hintReadonly') }}
                </template>
                <template v-else>
                  {{ $t('test.editor.jsonEditor.hintEditable') }}
                </template>
              </span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-snackbar
      v-model="snackbar"
      timeout="2000"
      :color="snackbarColor"
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          {{ $t('test.editor.jsonEditor.closeButton') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'TestJsonEditor',

  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    localJson: '',
    jsonError: '',
    isValidJson: true,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    editMode: false, // Di default in modalità solo lettura
  }),

  computed: {
    isReadonly() {
      return !this.editMode;
    },
  },

  watch: {
    value: {
      handler(newValue) {
        if (newValue) {
          this.localJson = JSON.stringify(newValue, null, 2);
          this.validateJson();
        }
      },
      immediate: true,
      deep: true,
    },

    editMode: {
      handler(newValue) {
        if (newValue) {
          // Quando si attiva la modalità modifica, mostra un avviso
          this.showSnackbar(this.$t('test.editor.jsonEditor.snackbar.editModeActivated'), 'warning');
        } else {
          // Quando si disattiva, mostra conferma
          this.showSnackbar(this.$t('test.editor.jsonEditor.snackbar.viewModeActivated'), 'info');
        }
      },
    },
  },

  methods: {
    onJsonChange() {
      // Se è in modalità readonly, mostra un avviso
      if (this.isReadonly) {
        this.showSnackbar(this.$t('test.editor.jsonEditor.snackbar.activateEditFirst'), 'warning');
        return;
      }

      // Valida in tempo reale ma non emette ancora l'evento
      this.validateJson();
    },

    validateAndEmit() {
      // Non emette cambiamenti se è in modalità readonly
      if (this.isReadonly) return;

      // Valida e se è valido, emette l'evento di cambiamento
      if (this.validateJson()) {
        try {
          const parsedConfig = JSON.parse(this.localJson);
          this.$emit('input', parsedConfig);
        } catch (error) {
          // Non dovrebbe succedere se validateJson ha ritornato true
          console.error('Errore inaspettato durante il parsing:', error);
        }
      }
    },

    validateJson() {
      if (!this.localJson.trim()) {
        this.jsonError = '';
        this.isValidJson = true;
        return true;
      }

      try {
        const parsed = JSON.parse(this.localJson);

        // Verifica che sia un array (configurazione del test)
        if (!Array.isArray(parsed)) {
          this.jsonError = this.$t('test.editor.jsonEditor.validationErrors.mustBeArray');
          this.isValidJson = false;
          return false;
        }

        // Verifica che abbia esattamente 4 stage
        if (parsed.length !== 4) {
          this.jsonError = this.$t('test.editor.jsonEditor.validationErrors.mustHaveFourStages');
          this.isValidJson = false;
          return false;
        }

        // Verifica la struttura base degli stage
        for (let i = 0; i < parsed.length; i++) {
          const stage = parsed[i];
          if (!stage.name || !stage.title) {
            this.jsonError = this.$t('test.editor.jsonEditor.validationErrors.stageMissingProps', { stageNumber: i + 1 });
            this.isValidJson = false;
            return false;
          }
        }

        this.jsonError = '';
        this.isValidJson = true;
        return true;
      } catch (error) {
        this.jsonError = error.message;
        this.isValidJson = false;
        return false;
      }
    },

    formatJson() {
      try {
        if (this.localJson.trim()) {
          const parsed = JSON.parse(this.localJson);
          this.localJson = JSON.stringify(parsed, null, 2);
          this.validateJson();
          this.showSnackbar(this.$t('test.editor.jsonEditor.snackbar.formatted'));
        }
      } catch (error) {
        this.showSnackbar(this.$t('test.editor.jsonEditor.snackbar.formatError'));
      }
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.localJson);
        this.showSnackbar(this.$t('test.editor.jsonEditor.snackbar.copied'));
      } catch (error) {
        // Fallback per browser che non supportano clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = this.localJson;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showSnackbar(this.$t('test.editor.jsonEditor.snackbar.copied'));
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.component-testjsoneditor {
  /* Stili del componente */
}

.json-editor >>> .v-textarea textarea {
  font-family: 'Roboto Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.json-editor >>> .v-input__control {
  border-radius: 4px;
}

.json-editor--readonly >>> .v-textarea textarea {
  background-color: #f5f5f5;
  color: #666;
  cursor: not-allowed;
}

.json-editor--readonly >>> .v-input__control {
  background-color: #f9f9f9;
  border: 2px dashed #ddd !important;
}

.json-editor--readonly >>> .v-label {
  color: #888 !important;
}

.json-editor--readonly >>> .v-icon {
  color: #888 !important;
}
</style>
